import { XSOLLA_CONFIG } from '../../../common/xsollaConfig';

/**
 * Xsolla Pay Station Widget CDN URL
 * @see https://developers.xsolla.com/doc/pay-station/integration/pay-station-widget-integration/
 */
const PAY_STATION_WIDGET_URL = 'https://static.xsolla.com/embed/paystation/1.3.1/widget.min.js';
const TRUSTED_PAY_STATION_ORIGINS = new Set(['https://secure.xsolla.com', 'https://sandbox-secure.xsolla.com']);

export type TXsollaWidgetStatus = 'new' | 'pending' | 'delivering' | 'done' | 'troubled' | 'unknown';

export interface IXsollaWidgetPaymentInfo {
  status?: TXsollaWidgetStatus;
  invoice?: number | string;
  payment_id?: number | string;
  external_id?: string;
  [key: string]: unknown;
}

export type TXsollaWidgetStatusEvent = {
  paymentInfo?: IXsollaWidgetPaymentInfo;
  [key: string]: unknown;
};

export type TXsollaNormalizedWidgetEvent =
  | { type: 'open'; command: string }
  | { type: 'stage'; command: string; data?: Record<string, unknown> }
  | { type: 'payment_info'; command: string; paymentInfo?: IXsollaWidgetPaymentInfo }
  | { type: 'success'; status: 'done'; paymentInfo: IXsollaWidgetPaymentInfo }
  | { type: 'pending'; status: 'pending' | 'delivering'; paymentInfo: IXsollaWidgetPaymentInfo }
  | { type: 'error'; status: 'troubled' | 'unknown'; paymentInfo: IXsollaWidgetPaymentInfo }
  | { type: 'close' };

type TXsollaWidgetLightboxOptions = {
  width?: string;
  height?: string;
  spinner?: string;
  spinnerColor?: string;
};

type TXsollaWidgetApi = {
  init: (options: { access_token: string; sandbox?: boolean; lightbox?: TXsollaWidgetLightboxOptions }) => void;
  on(event: 'status', callback: (event: TXsollaWidgetStatusEvent) => void): void;
  on(event: 'close', callback: () => void): void;
  off(event: 'status', callback: (event: TXsollaWidgetStatusEvent) => void): void;
  off(event: 'close', callback: () => void): void;
  open: () => void;
  close: () => void;
};

declare global {
  interface Window {
    XPayStationWidget?: TXsollaWidgetApi;
  }
}

let loadPromise: Promise<void> | null = null;

export function normalizeXsollaWidgetEvent(
  event?: TXsollaWidgetStatusEvent | null
): TXsollaNormalizedWidgetEvent | null {
  const paymentInfo = event?.paymentInfo;
  const status = paymentInfo?.status as TXsollaWidgetStatus | undefined;

  if (!paymentInfo) {
    return null;
  }

  switch (status) {
    case 'done':
      return { type: 'success', status, paymentInfo };
    case 'pending':
    case 'delivering':
      return { type: 'pending', status, paymentInfo };
    case 'troubled':
    case 'unknown':
      return { type: 'error', status, paymentInfo };
    default:
      return null;
  }
}

function parseWidgetPostMessageData(messageData: unknown): Record<string, any> | null {
  if (!messageData) {
    return null;
  }

  if (typeof messageData === 'string') {
    try {
      const parsed = JSON.parse(messageData);
      return typeof parsed === 'object' && parsed !== null ? parsed : null;
    } catch {
      return null;
    }
  }

  return typeof messageData === 'object' ? (messageData as Record<string, any>) : null;
}

function getPaymentInfoFromPostMessageData(data: Record<string, any>): IXsollaWidgetPaymentInfo | undefined {
  const nestedData = data.data || {};
  const paymentInfo = data.paymentInfo || data.payment_info || nestedData.paymentInfo || nestedData.payment_info;

  return paymentInfo && typeof paymentInfo === 'object' ? paymentInfo : undefined;
}

function isTrustedXsollaWidgetMessageOrigin(origin: string): boolean {
  return TRUSTED_PAY_STATION_ORIGINS.has(origin);
}

export function normalizeXsollaWidgetPostMessageEvent(messageData: unknown): TXsollaNormalizedWidgetEvent | null {
  const data = parseWidgetPostMessageData(messageData);
  const command = typeof data?.command === 'string' ? data.command : typeof data?.type === 'string' ? data.type : '';

  if (!data || !command) {
    return null;
  }

  if (command === 'open') {
    return { type: 'open', command };
  }

  if (command === 'close') {
    return { type: 'close' };
  }

  if (command === 'status') {
    const statusEvent = normalizeXsollaWidgetEvent({
      paymentInfo: getPaymentInfoFromPostMessageData(data)
    });
    if (statusEvent) {
      return statusEvent;
    }
  }

  const paymentInfo = getPaymentInfoFromPostMessageData(data);
  const lowerCommand = command.toLowerCase();
  if (
    lowerCommand.includes('click-btn-pay') ||
    lowerCommand.includes('payment-method') ||
    lowerCommand.includes('payment-submit')
  ) {
    return {
      type: 'payment_info',
      command,
      ...(paymentInfo ? { paymentInfo } : {})
    };
  }

  return {
    type: 'stage',
    command,
    ...(typeof data.data === 'object' && data.data !== null ? { data: data.data } : {})
  };
}

async function loadXsollaWidgetScript(): Promise<void> {
  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = new Promise((resolve, reject) => {
    if (window.XPayStationWidget) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = PAY_STATION_WIDGET_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      loadPromise = null;
      reject(new Error('Failed to load Xsolla widget script'));
    };
    document.head.appendChild(script);
  });

  return loadPromise;
}

async function loadXsollaWidget(): Promise<TXsollaWidgetApi> {
  await loadXsollaWidgetScript();

  const widget = window.XPayStationWidget;
  if (!widget) {
    loadPromise = null;
    throw new Error('XPayStationWidget not available after script load');
  }

  return widget;
}

export function createXsollaWidgetSessionOpener(deps: {
  loadWidget: () => Promise<TXsollaWidgetApi>;
  lightbox?: TXsollaWidgetLightboxOptions;
}) {
  const { loadWidget, lightbox } = deps;

  return async function openXsollaWidgetSession(options: {
    token: string;
    sandbox?: boolean;
    onEvent: (event: TXsollaNormalizedWidgetEvent) => void;
  }): Promise<() => void> {
    const { token, sandbox = false, onEvent } = options;
    const widget = await loadWidget();

    const handleStatus = (event: TXsollaWidgetStatusEvent) => {
      const normalizedEvent = normalizeXsollaWidgetEvent(event);
      if (normalizedEvent) {
        onEvent(normalizedEvent);
      }
    };
    const handleClose = () => {
      onEvent({ type: 'close' });
    };
    const handlePostMessage = (event: MessageEvent) => {
      if (!isTrustedXsollaWidgetMessageOrigin(event.origin)) {
        return;
      }

      const normalizedEvent = normalizeXsollaWidgetPostMessageEvent(event.data);
      if (normalizedEvent) {
        onEvent(normalizedEvent);
      }
    };

    widget.init({
      access_token: token,
      sandbox,
      lightbox
    });
    widget.on('status', handleStatus);
    widget.on('close', handleClose);
    window.addEventListener('message', handlePostMessage);
    widget.open();
    onEvent({ type: 'open', command: 'open' });

    return () => {
      try {
        window.removeEventListener('message', handlePostMessage);
        widget.off('status', handleStatus);
        widget.off('close', handleClose);
        widget.close();
      } catch {
        // Widget may already be closed or unavailable during teardown.
      }
    };
  };
}

export const openXsollaWidgetSession = createXsollaWidgetSessionOpener({
  loadWidget: loadXsollaWidget,
  lightbox: XSOLLA_CONFIG.payStation.lightbox
});
